// A desk's (or any full page's) identity in the host header: its name in both languages, its icon, the department it opened in and, for
// the nine process desks, its number and the modules it draws on (shell-ia §15.1, "useAppIdentity" moved from os/WindowChrome.tsx).
// WP-DESK-HOST.
//
// Sources, first that answers: the open session (the page as published, the space it opened in), the workspace's space manifest (the nav
// entry's label and icon), the page key. Nothing is invented: a page the catalogue does not know yet shows its key until its session opens.
import { useMemo } from "react";
import { useLocale } from "@ncp/i18n";
import type { PageSessionInfo } from "@ncp/page-host";
import type { LocalisedText } from "@ncp/page-sdk/types";
import type { ModuleKey } from "@ncp/page-sdk/desk";
import { useWorkspace } from "../../app/workspace-context";
import { useDepartments } from "../departments";
import { deskOf, manifestModules, type DeskInfo } from "./drawsOn";

export interface DeskIdentity {
  pageKey: string;
  /** The page's name in both languages. */
  name: LocalisedText;
  icon: string;
  /** The department (workspace) the page opened in, for the crumb; null until known. */
  space: { key: string; name: LocalisedText } | null;
  /** Set for the nine process desks only. */
  desk: DeskInfo | null;
  /** The modules the page draws on, primary first (the desk header's "Draws on" when the page publishes none). */
  drawsOn: { module: ModuleKey; primary: boolean }[];
}

export function useDeskIdentity(pageKey: string, space: string | null, session: PageSessionInfo | null): DeskIdentity {
  const { locale } = useLocale();
  const { workspaces, workspace } = useWorkspace();
  const departments = useDepartments();
  return useMemo(() => {
    const spaceKey = session?.space ?? space ?? workspace?.key ?? null;
    const ws = workspaces.find((w) => w.key === spaceKey) ?? (workspace?.key === spaceKey ? workspace : null);
    const nav = ws?.space?.nav.find((n) => n.page === pageKey) ?? null;
    const page = ws?.space?.pages.find((p) => p.key === pageKey) ?? null;
    const name: LocalisedText = session
      ? { en: session.page.name, ...(session.page.nameAr ? { ar: session.page.nameAr } : {}) }
      : nav?.label
        ? { en: nav.label.en, ...(nav.label.ar ? { ar: nav.label.ar } : {}) }
        : page
          ? { en: page.name, ...(page.nameAr ? { ar: page.nameAr } : {}) }
          : { en: pageKey };
    const desk = deskOf(pageKey);
    const declared = manifestModules(nav) ?? (desk ? { primary: desk.primary, secondary: desk.secondary } : null);
    const drawsOn = declared ? [...declared.primary.map((m) => ({ module: m, primary: true })), ...declared.secondary.map((m) => ({ module: m, primary: false }))] : [];
    return {
      pageKey,
      name,
      icon: nav?.icon ?? session?.page.icon ?? page?.icon ?? "AppWindow",
      // The department's own name (the departments model, SHELL; the same words as the sidebar and the switcher), else the workspace's.
      space: ws ? { key: ws.key, name: departmentName(departments.all.find((u) => u.workspace === ws.key)) ?? { en: ws.name, ...(ws.nameAr ? { ar: ws.nameAr } : {}) } } : null,
      desk,
      drawsOn,
    };
    // locale only re-renders consumers; the identity itself is bilingual
  }, [pageKey, space, session, workspaces, workspace, locale, departments.all]);
}

const departmentName = (u: { name: { en: string; ar?: string | null } } | undefined): LocalisedText | null =>
  u?.name?.en ? { en: u.name.en, ...(u.name.ar ? { ar: u.name.ar } : {}) } : null;

/**
 * The nine desks' names from the space manifest that mounts them (its nav labels), for the desk switcher; a desk the person cannot reach is
 * left out of the map and the switcher shows its number only.
 */
export function useDeskNames(): Record<string, LocalisedText> {
  const { workspaces } = useWorkspace();
  return useMemo(() => {
    const out: Record<string, LocalisedText> = {};
    for (const w of workspaces) {
      for (const n of w.space?.nav ?? []) {
        if (!n.page || out[n.page] || !deskOf(n.page) || !n.label) continue;
        out[n.page] = { en: n.label.en, ...(n.label.ar ? { ar: n.label.ar } : {}) };
      }
    }
    return out;
  }, [workspaces]);
}
